/**
 * Sync-telemetry anti-flood guards + schema.
 *
 * The single most important property of sync telemetry: it must be IMPOSSIBLE
 * to flood the shared crash pipeline, because the bug we're measuring is a sync
 * *loop*. A loop that hits a telemetry call thousands of times must emit at most
 * a tiny, bounded number of events. Four independent guards enforce that, and a
 * fifth (schema validation) keeps malformed payloads off the wire:
 *
 *  1. PER-`syncRunId` DEDUPE — the same dedupe key is emitted at most ONCE per
 *     sync run. The dedupe key is the eventName, EXCEPT for `chat_list_incomplete`
 *     which is additionally discriminated by its BOUNDED `data.type` ∈
 *     {group, p2p} (one chat list per type per run can starve independently, and
 *     collapsing the p2p signal into the group one would lose exactly the signal
 *     this telemetry exists to capture). The discriminator is BOUNDED to a tiny
 *     fixed set, so the per-run key count stays bounded (≤2 chat_list_incomplete
 *     /run) and the loop defense is intact — unbounded `data` is never keyed on.
 *     Repeats of the same key within a run are dropped (and tallied). This is the
 *     primary loop defense: a run that re-triggers the same anomaly 5,000 times
 *     emits exactly ONE event. NOTE: because the FIRST occurrence emits
 *     immediately, the tally of later same-run repeats has no second event to
 *     ride on, so `suppressedCount` is surfaced only in the rarer case where a
 *     repeat was collapsed BEFORE the first event was allowed (e.g. the first
 *     occurrence lost a rate-limit/cap race). The flood bound does not depend on
 *     surfacing the count — the dedupe drop itself is the guarantee.
 *  2. PER-EVENT-TYPE RATE LIMIT — across runs, ≤1 identical eventName per
 *     RATE_LIMIT_WINDOW_MS (10 min), persisted in localStorage so it survives a
 *     reload-crash loop.
 *  3. PER-CLIENT DAILY CAP — ≤ DAILY_CAP events/client/day. Once hit, further
 *     events are dropped and a `droppedToday` counter accrues, surfaced on the
 *     next allowed event so the gap is visible (not silent).
 *  4. PAYLOAD SIZE CAP — enforced by the reporter after this module (it owns the
 *     serialized form); this module keeps `data` small by construction.
 *  5. SCHEMA VALIDATION — reject envelopes missing required fields / wrong types
 *     / unknown eventName before they can reach the queue.
 *
 * State (rate-limit timestamps, dedupe set, daily counter) is persisted in
 * localStorage. All localStorage access is defensive (private mode / quota):
 * on failure the guards FAIL CLOSED for the persisted checks where appropriate
 * but never throw — the reporter wraps the whole thing anyway.
 */

import { safeStorage } from "@/utils/safeStorage";

export const SCHEMA_VERSION = 1 as const;
export const TELEMETRY_TAG = "[SyncTelemetry]" as const;

/** All recognized event names. Anything else fails schema validation. */
export const SYNC_TELEMETRY_EVENTS = [
  "full_resync_started",
  "full_resync_completed",
  "full_resync_incomplete",
  "chat_list_incomplete",
  "resync_event_catchup_incomplete",
  "prune_skipped_unsent",
  "scheduler_timeout",
  "sync_run_summary",
  "sync_telemetry_summary",
  // WS2 Phase 2a — window-priority hydration. Routine (NOT anomaly): counts +
  // timing only (windowSize, windowCandidateCount, msToWindowPreviewHydrated,
  // path). NO PII. Named `..._preview_hydrated` (not `..._fresh`): the early
  // preview can show a stale/edited/deleted last message until the event drain
  // reaches it, so the metric measures ONLY that previews were decrypted (F5).
  "window_preview_hydrated",
  // PR-A1 sync-event-cursor-deadlock (flag syncEventDrainV2). Counts only,
  // NO PII (never chat ids / cursors):
  //  - starved_requeue: a /sync/events chunk had chats starved by the BE
  //    global limit (hasMore with no cursor) and ran bounded follow-up
  //    batches — {rounds, starvedCount, remainingCount}.
  //  - event_gap_expired_at_migration: chats seeded from the legacy cursor
  //    had it clamped to the TTL floor (their owed events already expired
  //    server-side) — {chatCount}.
  "starved_requeue",
  "event_gap_expired_at_migration",
  // Telemetry PR (sync-event-cursor-deadlock §4, flag syncEventDrainV2).
  // Counts only, NO PII (never chat ids / cursors / timestamps):
  //  - checkpoint_resume: fired at run START when the persisted per-chat map
  //    holds >=1 STREAM-format checkpoint — the previous run was interrupted
  //    mid-pagination and this run RESUMES from the checkpoint instead of
  //    replaying the window. Anomaly-class (unsampled numerator) — payload
  //    {resumedCheckpoints}. NOTE: oldest_cursor_age / laggard_count / the
  //    drainVersion split ride the existing SAMPLED `sync_run_summary` (fleet
  //    percentile metrics, 2% sampling acceptable) — they are payload fields,
  //    NOT their own event names.
  "checkpoint_resume",
] as const;

export type SyncTelemetryEventName = (typeof SYNC_TELEMETRY_EVENTS)[number];

const EVENT_NAME_SET = new Set<string>(SYNC_TELEMETRY_EVENTS);

export interface SyncTelemetryEnvelope {
  schemaVersion: typeof SCHEMA_VERSION;
  eventName: SyncTelemetryEventName;
  timestamp: string;
  appVersion: string;
  syncRunId: string;
  clientId: string;
  accountSizeBucket: string;
  networkState: { online: boolean; effectiveType?: string };
  data: Record<string, string | number | boolean | null | undefined>;
  /** Annotated by the dedupe guard: repeats collapsed within this syncRunId. */
  suppressedCount?: number;
  /** Annotated by the daily-cap guard: events dropped since the last allowed one. */
  droppedToday?: number;
}

// --- Tuning -----------------------------------------------------------------

/** Per-event-type rate limit window (≤1 identical eventName per window). */
const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000; // 10 min
/** Soft daily cap: routine (non-anomaly) events are dropped past this. */
const DAILY_CAP = 20;
/**
 * HARD daily ceiling — applies to EVERYTHING, anomalies included. Anomaly /
 * timeout events bypass the soft cap + rate limit so we keep numerator truth,
 * but a sync LOOP that re-invokes `runSync()` rapidly mints a fresh syncRunId
 * each run and would otherwise emit one anomaly PER run with no absolute bound
 * (Codex HIGH). This ceiling caps total telemetry per client/day regardless of
 * event class, so the worst case is bounded (≈ a few dozen events/day, well
 * within the feedback pipeline's tolerance) — the flood is structurally
 * impossible. Set well above DAILY_CAP so genuine anomaly bursts are still
 * captured before the ceiling bites.
 */
const HARD_DAILY_CEILING = 60;

const RATE_KEY = "syncTelemetryRateLimit"; // { [eventName]: lastSentEpochMs }
const DEDUPE_KEY = "syncTelemetryRunDedupe"; // { runId, names: string[] }
const DAILY_KEY = "syncTelemetryDaily"; // { day: "YYYY-MM-DD", count, dropped }

// --- localStorage helpers (defensive) ---------------------------------------

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = safeStorage.get(key);
    if (!raw) {
      return fallback;
    }
    const parsed = JSON.parse(raw);
    return (parsed ?? fallback) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    safeStorage.set(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — drop silently */
  }
}

// --- Schema validation -------------------------------------------------------

/**
 * Validate the envelope's required fields + types and the eventName allowlist.
 * Returns true only for a well-formed envelope; the reporter drops it otherwise.
 */
export function validateEnvelope(env: SyncTelemetryEnvelope): boolean {
  if (env.schemaVersion !== SCHEMA_VERSION) {
    return false;
  }
  if (typeof env.eventName !== "string" || !EVENT_NAME_SET.has(env.eventName)) {
    return false;
  }
  if (typeof env.timestamp !== "string" || env.timestamp.length === 0) {
    return false;
  }
  if (typeof env.appVersion !== "string") {
    return false;
  }
  if (typeof env.syncRunId !== "string" || env.syncRunId.length === 0) {
    return false;
  }
  if (typeof env.clientId !== "string" || env.clientId.length === 0) {
    return false;
  }
  if (typeof env.accountSizeBucket !== "string") {
    return false;
  }
  if (
    typeof env.networkState !== "object" ||
    env.networkState === null ||
    typeof env.networkState.online !== "boolean"
  ) {
    return false;
  }
  if (typeof env.data !== "object" || env.data === null) {
    return false;
  }
  // Defense-in-depth (Codex MED): the typed `reportSyncTelemetry` surface already
  // blocks object values, but `data` keys are caller-supplied. Reject the whole
  // envelope if any data KEY looks identifying (chat/user/cursor/error/title/...)
  // or any VALUE is a non-primitive — so a future caller cannot accidentally ship
  // PII through this path even if the type checker is bypassed.
  for (const [key, value] of Object.entries(env.data)) {
    if (PII_KEY_PATTERN.test(key)) {
      return false;
    }
    if (
      value !== null &&
      value !== undefined &&
      typeof value !== "string" &&
      typeof value !== "number" &&
      typeof value !== "boolean"
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Key-name denylist for the `data` bag. Targets the identifying field families
 * we never want in telemetry while NOT tripping on legitimate count/duration
 * keys the engine actually sends (removedCount, fetchedCount, tokensAvailable,
 * cooldownRemainingMs, etc.). Matched case-insensitively. We deliberately match
 * id-like SUFFIXES (`...Id`, `...Ids`) rather than the bare substring "id" so
 * "valid"/"avoid"/etc. don't false-positive, and `chatId`/`participantIds`/
 * `serverCursor`/`rawError`/`username`/`messageTitle`/`authToken` all trip it.
 */
const PII_KEY_PATTERN =
  /(chatid|messageid|msgid|participant|username|sender|recipient|displayname|title|cursor|authtoken|accesstoken|refreshtoken|apikey|secret|password|rawerror|errormessage|stacktrace|\bemail\b|\bphone\b|address|[a-z0-9]+ids?\b)/i;

// --- Anomaly classification --------------------------------------------------

/**
 * Anomaly / timeout events are NOT rate-limited or daily-capped away as
 * aggressively — we need numerator truth (you can't measure "how often does the
 * resync get starved" if you drop the starvation events). They still go through
 * the per-run dedupe (so a loop can't flood) but BYPASS the cross-run rate limit
 * and are exempt from being DROPPED by the daily cap (they still increment the
 * counter for visibility). Routine/summary events are fully gated.
 */
const ANOMALY_EVENTS = new Set<SyncTelemetryEventName>([
  "full_resync_started",
  "full_resync_completed",
  "full_resync_incomplete",
  "chat_list_incomplete",
  "resync_event_catchup_incomplete",
  // A skipped prune is a near-miss data-loss signal we must always capture
  // (bypass the cross-run rate limit); the per-run dedupe still bounds a loop.
  "prune_skipped_unsent",
  "scheduler_timeout",
  // Both are numerator-truth signals for the event-drain-v2 rollout: starved
  // re-queue sizing and the one-time migration gap count must not be sampled
  // or rate-limited away (the per-run dedupe + hard daily ceiling still bound
  // a loop).
  "starved_requeue",
  "event_gap_expired_at_migration",
  // Interrupted-run resume proof: must not be sampled/rate-limited away (it is
  // exactly the numerator for "how often do runs resume vs replay"). The
  // per-run dedupe + hard daily ceiling still bound any loop.
  "checkpoint_resume",
]);

function isAnomaly(name: SyncTelemetryEventName): boolean {
  return ANOMALY_EVENTS.has(name);
}

// --- Dedupe key derivation (BOUNDED discriminators only) --------------------

/**
 * `chat_list_incomplete` can legitimately fire twice in ONE sync run — once for
 * the group chat list and once for the p2p list (they paginate as separate
 * server collections and can starve independently). Keying dedupe on bare
 * eventName would collapse the second into the first, silently losing the p2p
 * starvation signal precisely when BOTH lists are incomplete — the exact case
 * this telemetry exists to diagnose. So this event's dedupe key is discriminated
 * by its `data.type`.
 *
 * CRITICAL — the discriminator MUST stay BOUNDED so it can't become a flood
 * vector: only the two known list types ({group, p2p}) widen the key; ANY other
 * / missing / non-string value folds into a single `"other"` bucket. Worst case:
 * 3 distinct chat_list_incomplete keys per run regardless of caller input — the
 * per-run dedupe (and thus the loop defense) stays bounded. We deliberately do
 * NOT key on any other `data` field (those are unbounded counts/reasons).
 */
const CHAT_LIST_INCOMPLETE_TYPES = new Set(["group", "p2p"]);

function dedupeKeyFor(
  eventName: SyncTelemetryEventName,
  data: SyncTelemetryEnvelope["data"],
): string {
  if (eventName === "chat_list_incomplete") {
    const type = data?.type;
    const discriminator =
      typeof type === "string" && CHAT_LIST_INCOMPLETE_TYPES.has(type)
        ? type
        : "other";
    return `${eventName}:${discriminator}`;
  }
  return eventName;
}

// --- Guard 1: per-syncRunId dedupe ------------------------------------------

/**
 * Returns the number of times this (runId, dedupeKey) pair has ALREADY been
 * seen this run (0 on the first sight), and records the sighting. The reporter
 * emits only when this returns 0, attaching `suppressedCount` from the running
 * tally at the next allowed window — but because the rate limit already blocks
 * cross-run repeats, the practical contract is: FIRST occurrence in a run emits,
 * all later occurrences in the SAME run are suppressed and counted.
 *
 * Stored as a single { runId, names, suppressed } record so a new run resets it
 * (we only keep the current run's set, bounded by the fixed event-name count).
 */
interface RunDedupeState {
  runId: string;
  names: string[];
  /** dedupeKey → count of suppressed repeats in this run. */
  suppressed: Record<string, number>;
}

function dedupeForRun(
  runId: string,
  dedupeKey: string,
): { firstInRun: boolean; suppressedCount: number } {
  const state = readJson<RunDedupeState>(DEDUPE_KEY, {
    runId,
    names: [],
    suppressed: {},
  });
  // New run → reset.
  if (state.runId !== runId) {
    const next: RunDedupeState = { runId, names: [dedupeKey], suppressed: {} };
    writeJson(DEDUPE_KEY, next);
    return { firstInRun: true, suppressedCount: 0 };
  }
  if (!state.names.includes(dedupeKey)) {
    state.names.push(dedupeKey);
    writeJson(DEDUPE_KEY, state);
    return { firstInRun: true, suppressedCount: 0 };
  }
  // Repeat within the same run — collapse: bump the suppressed tally and report
  // back the new total so a (rare) later-allowed event can carry it.
  state.suppressed[dedupeKey] = (state.suppressed[dedupeKey] ?? 0) + 1;
  writeJson(DEDUPE_KEY, state);
  return { firstInRun: false, suppressedCount: state.suppressed[dedupeKey] };
}

// --- Guard 2: per-event-type rate limit -------------------------------------

function rateLimited(eventName: SyncTelemetryEventName, now: number): boolean {
  const state = readJson<Record<string, number>>(RATE_KEY, {});
  const last = state[eventName] ?? 0;
  if (Number.isFinite(last) && now - last < RATE_LIMIT_WINDOW_MS) {
    return true;
  }
  state[eventName] = now;
  writeJson(RATE_KEY, state);
  return false;
}

// --- Guard 3: per-client daily cap ------------------------------------------

interface DailyState {
  day: string;
  count: number;
  dropped: number;
}

function utcDay(now: number): string {
  return new Date(now).toISOString().slice(0, 10); // YYYY-MM-DD (UTC)
}

/**
 * Returns whether the event is allowed under the daily cap and, if allowed, the
 * number of events DROPPED since the last allowed one (to surface as
 * `droppedToday`). Anomaly events are never *dropped* by the cap (we need the
 * numerator) but still increment the count so the cap reflects true volume.
 */
function dailyCap(
  eventName: SyncTelemetryEventName,
  now: number,
): { allowed: boolean; droppedToday: number } {
  const today = utcDay(now);
  const state = readJson<DailyState>(DAILY_KEY, {
    day: today,
    count: 0,
    dropped: 0,
  });
  if (state.day !== today) {
    // New UTC day — reset.
    state.day = today;
    state.count = 0;
    state.dropped = 0;
  }

  // HARD ceiling first — bounds EVERY event class (incl. anomalies) so a loop
  // that mints a fresh syncRunId per run can't emit unboundedly (Codex HIGH).
  if (state.count >= HARD_DAILY_CEILING) {
    state.dropped += 1;
    writeJson(DAILY_KEY, state);
    return { allowed: false, droppedToday: state.dropped };
  }

  const overCap = state.count >= DAILY_CAP;
  if (overCap && !isAnomaly(eventName)) {
    // Routine event over the SOFT cap — drop and count it (anomalies keep going
    // up to the hard ceiling above so we retain numerator truth).
    state.dropped += 1;
    writeJson(DAILY_KEY, state);
    return { allowed: false, droppedToday: state.dropped };
  }

  // Allowed (under cap, or an anomaly we refuse to drop). Emit any accrued
  // `dropped` count with this event, then reset it.
  const droppedToday = state.dropped;
  state.count += 1;
  state.dropped = 0;
  writeJson(DAILY_KEY, state);
  return { allowed: true, droppedToday };
}

/**
 * Record a drop that happened for a reason OTHER than the daily cap (currently:
 * a routine event suppressed by the cross-run rate limit) so it still accrues
 * into `droppedToday` and is surfaced on the next allowed event — no silent
 * holes in the dropped count. Anomaly events are never rate-limited, so they
 * never reach here.
 */
function recordExtraDrop(now: number): void {
  const today = utcDay(now);
  const state = readJson<DailyState>(DAILY_KEY, {
    day: today,
    count: 0,
    dropped: 0,
  });
  if (state.day !== today) {
    state.day = today;
    state.count = 0;
    state.dropped = 0;
  }
  state.dropped += 1;
  writeJson(DAILY_KEY, state);
}

// --- Orchestrator ------------------------------------------------------------

/**
 * Run all guards in order and return the (annotated) envelope to send, or null
 * to drop it. Order matters: schema first (cheapest reject), then per-run dedupe
 * (the loop defense — runs for EVERY call so the suppressed tally is accurate),
 * then the cross-run rate limit (routine events only), then the daily cap.
 */
export function passesGuards(
  env: SyncTelemetryEnvelope,
  now: number = Date.now(),
): SyncTelemetryEnvelope | null {
  if (!validateEnvelope(env)) {
    return null;
  }

  // Guard 1: per-run dedupe. Always recorded so suppressedCount stays accurate.
  // The dedupe key is the eventName, BOUNDED-discriminated by data.type for
  // chat_list_incomplete so the group and p2p lists each emit once per run.
  const dedupeKey = dedupeKeyFor(env.eventName, env.data);
  const { firstInRun } = dedupeForRun(env.syncRunId, dedupeKey);
  if (!firstInRun) {
    // Collapsed into the run's suppressed tally — do not emit a second time.
    return null;
  }

  // Guard 2: cross-run rate limit. Anomaly/timeout events bypass it (numerator
  // truth) — they still can't flood because Guard 1 caps them to once per run.
  if (!isAnomaly(env.eventName) && rateLimited(env.eventName, now)) {
    // Still count it as a drop so `droppedToday` has no silent holes.
    recordExtraDrop(now);
    return null;
  }

  // Guard 3: daily cap.
  const { allowed, droppedToday } = dailyCap(env.eventName, now);
  if (!allowed) {
    return null;
  }

  // Surface any prior suppressed repeats for THIS run (≥1 only if a prior call
  // in the same run was collapsed before this first-allowed emit — rare, but
  // keeps the count honest) and any daily drops since the last allowed event.
  const suppressed = readJson<RunDedupeState>(DEDUPE_KEY, {
    runId: env.syncRunId,
    names: [],
    suppressed: {},
  }).suppressed[dedupeKey];
  if (suppressed && suppressed > 0) {
    env.suppressedCount = suppressed;
  }
  if (droppedToday > 0) {
    env.droppedToday = droppedToday;
  }
  return env;
}

/** Test-only: clear all persisted guard state. */
export function _resetSyncTelemetryGuards(): void {
  try {
    safeStorage.remove(RATE_KEY);
    safeStorage.remove(DEDUPE_KEY);
    safeStorage.remove(DAILY_KEY);
  } catch {
    /* ignore */
  }
}
